import DynamicList from "../components/DynamicList";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";

interface ListsTabProps {
  financials: any[];
  peers: any[];
  objectives: any[];
  promoters: any[];
  setFinancials: (items: any[]) => void;
  setPeers: (items: any[]) => void;
  setObjectives: (items: any[]) => void;
  setPromoters: (items: any[]) => void;
  /** Matches API companyFinancials.amountUnit (e.g. ₹ Crore) */
  financialAmountUnit?: string;
  /** Matches API ipoObjectsOfIssue.amountUnit (e.g. ₹ Cr.) */
  objectsOfIssueAmountUnit?: string;
  readOnly?: boolean;
}

const ListsTab = ({
  financials,
  objectives,
  promoters,
  setFinancials,
  setObjectives,
  setPromoters,
  financialAmountUnit = "",
  objectsOfIssueAmountUnit = "",
  readOnly = false,
}: ListsTabProps) => {
  const formatIndianAmount = (value: string | number | null | undefined) => {
    const num = Number(value ?? 0);
    return Number.isNaN(num) ? "0" : num.toLocaleString("en-IN");
  };

  return (
    <>
      <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3">
        <Label className="text-xs font-semibold text-slate-600">
          Amount unit (companyFinancials)
        </Label>
        <Input
          value={financialAmountUnit}
          readOnly
          placeholder="e.g. ₹ Crore"
          className="mt-1.5 h-9 max-w-md cursor-not-allowed border-slate-200 bg-muted/50 text-xs"
        />
      </div>
      <DynamicList
        label="Financial Performance (Summary)"
        subtitle="Rows map to companyFinancials.financialByPeriod"
        items={financials}
        onChange={setFinancials}
        indianFormatNumberFields={[
          "total_income",
          "assets",
          "ebitda",
          "profit_after_tax",
          "net_worth",
          "reserves_and_surplus",
          "total_borrowing",
        ]}
        readOnly={readOnly}
        fields={[
          { key: "period_ended", label: "Period ended", type: "text" },
          {
            key: "total_income",
            label: "Total income",
            type: "number",
          },
          { key: "assets", label: "Assets", type: "number" },
          { key: "ebitda", label: "EBITDA", type: "number" },
          {
            key: "profit_after_tax",
            label: "PAT",
            type: "number",
          },
          { key: "net_worth", label: "Net worth", type: "number" },
          {
            key: "reserves_and_surplus",
            label: "Reserves & surplus",
            type: "number",
          },
          {
            key: "total_borrowing",
            label: "Total borrowing",
            type: "number",
          },
        ]}
      />

      {/* <DynamicList
        label="Peer Comparison"
        items={peers}
        onChange={setPeers}
        readOnly={readOnly}
        fields={[
          { key: "company_name", label: "Company Name", type: "text" },
          { key: "pe", label: "P/E", type: "number" },
          { key: "eps_basic", label: "EPS (Basic)", type: "number" },
          { key: "nav", label: "NAV", type: "number" },
          { key: "ronw", label: "RoNW %", type: "number" },
        ]}
      /> */}

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-2">
            <Label className="text-xs font-semibold text-slate-600">
              Amount unit (ipoObjectsOfIssue)
            </Label>
            <Input
              value={objectsOfIssueAmountUnit}
              readOnly
              placeholder="e.g. ₹ Cr."
              className="mt-1.5 h-9 max-w-md cursor-not-allowed border-slate-200 bg-muted/50 text-xs"
            />
          </div>
          <DynamicList
            label="Objects of the Issue"
            subtitle="Rows map to ipoObjectsOfIssue.items"
            items={objectives}
            onChange={setObjectives}
            indianFormatNumberFields={["estimatedAmount"]}
            readOnly={readOnly}
            fields={[
              { key: "serialNo", label: "S.No", type: "number" },
              {
                key: "issueObject",
                label: "Issue object",
                type: "text",
              },
              {
                key: "estimatedAmount",
                label: "Est. amount",
                type: "number",
              },
            ]}
            footer={
              <div className="flex items-center justify-between px-3 py-1.5 transition-all">
                <div className="flex items-center gap-2 text-slate-500">
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    Total Objects Val
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-black text-primary">
                    {objectsOfIssueAmountUnit.startsWith("₹") ? "" : "₹"}
                    {formatIndianAmount(
                      objectives.reduce(
                        (sum, obj) => sum + (Number(obj.estimatedAmount) || 0),
                        0
                      )
                    )}
                    {objectsOfIssueAmountUnit && ` ${objectsOfIssueAmountUnit}`}
                  </span>
                </div>
              </div>
            }
          />
        </div>
        <DynamicList
          label="Company Promoters"
          items={promoters}
          onChange={setPromoters}
          readOnly={readOnly}
          fields={[{ key: "name", label: "Promoter Name", type: "text" }]}
        />
      </div>
    </>
  );
};

export default ListsTab;
